import { initRawSchema } from '@principle-theorem/editor';
import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  IDestinationEntity,
  IDestinationEntityJobRunOptions,
  IDestinationEntityRecord,
  IGetRecordResponse,
  IPracticeMigration,
  SkippedDestinationEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  IStaffer,
  ISterilisationCycle,
  ISterilisationMachine,
  SterilisationStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  WithRef,
  getError,
  snapshotCombineLatest,
  toNamedDocument,
} from '@principle-theorem/shared';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { resolveMappedItem } from '../../../base-custom-mapping-handler';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import {
  BaseSterilisationCycleDestinationEntity,
  ISterilisationCycleJobData,
  ISterilistionCycleRecordMigrationData,
} from '../../../destination/entities/sterilisation-cycle';
import { STERILISATION_MACHINE_RESOURCE_TYPE } from '../../../mappings/sterilisation-machine';
import { TranslationMapHandler } from '../../../translation-map';
import {
  D4WSterilisationCycle,
  D4WSterilisationCycleTestStatus,
  ID4WSterilisationCycle,
  SterilisationCyclesSourceEntity,
} from '../../source/entities/sterilisation-cycles';
import { D4WStafferMappingHandler, resolveD4WStaffer } from '../mappings/staff';
import { D4WSterilisationMachineMappingHandler } from '../mappings/sterilisation-machine';
import { StafferDestinationEntity } from './staff';
import { D4WSterilisationMachineDestinationEntity } from './sterilisation-machine';

interface ISteriliserJobCycleData
  extends ISterilisationCycleJobData<D4WSterilisationCycle> {
  sourceSterilisationCycle: IGetRecordResponse<D4WSterilisationCycle>;
}

export class D4WSterilisationCycleDestinationEntity extends BaseSterilisationCycleDestinationEntity<D4WSterilisationCycle> {
  sterilisationCycleSourceEntity = new SterilisationCyclesSourceEntity();
  sterilisationMachineMappings = new D4WSterilisationMachineMappingHandler();

  override sourceEntities = {
    sterilisationCycle: new SterilisationCyclesSourceEntity(),
  };

  customMappings = {
    sterilisationMachineMapping: this.sterilisationMachineMappings,
    staff: new D4WStafferMappingHandler(),
  };

  override destinationEntities = {
    staff: new StafferDestinationEntity(),
    sterilisationMachines: new D4WSterilisationMachineDestinationEntity(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<ISterilisationCycleJobData<D4WSterilisationCycle>[]> {
    const sterilisationMachineMappings$ =
      this.sterilisationMachineMappings.getRecords(translationMap);
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));

    return snapshotCombineLatest([sterilisationMachineMappings$, staff$])
      .pipe(
        switchMap((dependencies) =>
          combineLatest([
            this.buildSourceRecordQuery$(
              migration,
              this.sterilisationCycleSourceEntity,
              runOptions
            ),
            of(dependencies),
          ])
        )
      )
      .pipe(
        map(
          ([
            sourceSterilisationCycles,
            [sterilisationMachineMappings, staff],
          ]) =>
            sourceSterilisationCycles.map((sourceSterilisationCycle) => ({
              sourceSterilisationCycle,
              sterilisationMachineMappings,
              staff,
            }))
        )
      );
  }

  async buildMigrationData(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: ISteriliserJobCycleData
  ): Promise<
    | ISterilistionCycleRecordMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    const sourceSteriliserCycle = data.sourceSterilisationCycle;
    const sourceSteriliserCycleId = this.sterilisationCycleSourceEntity
      .getSourceRecordId(sourceSteriliserCycle.data)
      .toString();

    try {
      const machine = await resolveMappedItem<ISterilisationMachine>(
        data.sourceSterilisationCycle.data.machineId.toString(),
        data.sterilisationMachineMappings,
        translationMap,
        STERILISATION_MACHINE_RESOURCE_TYPE
      );

      const staffer = await resolveD4WStaffer(
        data.sourceSterilisationCycle.data.stafferId.toString(),
        data.staff,
        translationMap,
        false
      );

      const cycleRecord: ISterilisationCycle = {
        id: sourceSteriliserCycle.data.cycleId,
        machine: toNamedDocument(machine),
        staffer,
        name: sourceSteriliserCycle.data.cycleId,
        runDate: sourceSteriliserCycle.record.translations.runDate,
        notes: initRawSchema(sourceSteriliserCycle.data.notes),
        status: this._getSterilisationCycleStatus(sourceSteriliserCycle.data),
        deleted: false,
      };

      return {
        sterilisationCycleRecord: {
          ...cycleRecord,
          sourceIdentifier: sourceSteriliserCycleId,
          createdAt: data.sourceSterilisationCycle.record.translations.runDate,
        },
        practiceRef: machine.practice.ref,
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.sourceSterilisationCycle,
        getError(error)
      );
    }
  }

  buildSkippedResponse(
    data: ISterilisationCycleJobData<D4WSterilisationCycle>
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: data.sourceSterilisationCycle.record.uid,
      label: data.sourceSterilisationCycle.record.label,
      status: DestinationEntityRecordStatus.Skipped,
      sourceRef: data.sourceSterilisationCycle.record.id,
    };
  }

  private _getSterilisationCycleStatus(
    sourceCycle: ID4WSterilisationCycle
  ): SterilisationStatus {
    if (sourceCycle.isTested === D4WSterilisationCycleTestStatus.Tested) {
      return sourceCycle.isPassed === D4WSterilisationCycleTestStatus.Tested
        ? SterilisationStatus.Passed
        : SterilisationStatus.Failed;
    }
    return SterilisationStatus.Passed;
  }
}
